import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CircleHelp,
  Loader2,
  PillBottle,
  Sparkles,
  X,
} from "lucide-react";
import { useCompanyContext } from "../../context/CompanyContext";
import { searchRx } from "../../api/cases";
import { Input } from "../Universal/Input";
import { Button } from "../Universal/Button";

// ---------------------------------------------------------------------------
// Confidence — the endpoint returns "high" | "medium" | "low".
// ---------------------------------------------------------------------------
const LEVELS = ["low", "medium", "high"];

export function confidenceTier(raw) {
  const label = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  return LEVELS.includes(label) ? label : "unknown";
}

const TIERS = {
  high: {
    label: "Strong match",
    hint: "This looks like a known Rx.",
    Icon: Check,
    text: "text-activeCase",
    fill: "bg-activeCase",
  },
  medium: {
    label: "Likely match",
    hint: "Close, but double-check the recommended name.",
    Icon: Sparkles,
    text: "text-sunColor",
    fill: "bg-sunColor",
  },
  low: {
    label: "Weak match",
    hint: "We couldn't confidently match this. Review before adding.",
    Icon: AlertTriangle,
    text: "text-logoutColor",
    fill: "bg-logoutColor",
  },
  unknown: {
    label: "No confidence score",
    hint: "The service didn't return a confidence level.",
    Icon: CircleHelp,
    text: "text-mutedText",
    fill: "bg-mutedText",
  },
};

function ConfidenceMeter({ tier }) {
  const { label, hint, Icon, text, fill } = TIERS[tier];
  const lit = LEVELS.indexOf(tier) + 1; // low=1, medium=2, high=3, unknown=0

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div className={`flex items-center gap-1.5 text-sm font-light ${text}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
          <span className="text-mainText">{label}</span>
        </div>
        <span className="text-xs uppercase tracking-wide text-mutedText">
          {tier === "unknown" ? "—" : `${tier} confidence`}
        </span>
      </div>
      <div
        role="meter"
        aria-label="Match confidence"
        aria-valuemin={0}
        aria-valuemax={LEVELS.length}
        aria-valuenow={lit}
        aria-valuetext={tier === "unknown" ? "Unknown" : `${tier} — ${label}`}
        className="mt-2 flex gap-1"
      >
        {LEVELS.map((level, i) => (
          <span
            key={level}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
              i < lit ? fill : "bg-mainDivider"
            }`}
            style={i < lit ? { transitionDelay: `${i * 60}ms` } : undefined}
          />
        ))}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-mutedText">{hint}</p>
    </div>
  );
}

// Unwrap the response body. Tolerates a bare object, an array of one match,
// or a `{ data: {...} }` / `{ result: {...} }` envelope.
// An empty array means the service found nothing; that returns `null` and
// the caller shows the "no match" state.
function extractMatch(res) {
  let body = res?.data ?? res;
  if (Array.isArray(body)) body = body[0];
  if (body && typeof body === "object") {
    if (body.data && typeof body.data === "object" && "recommended_name" in body.data)
      body = body.data;
    else if (body.result && typeof body.result === "object" && "recommended_name" in body.result)
      body = body.result;
  }
  if (!body || typeof body !== "object") return null;
  return {
    inputName: String(body.input_name ?? "").trim(),
    recommendedName: String(body.recommended_name ?? "").trim(),
    tier: confidenceTier(body.confidence),
    raw: body,
  };
}

const sameName = (a, b) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

// ---------------------------------------------------------------------------

const PopUpSearch = () => {
  const {
    companies,
    addCompany,
    selectCompany,
    refreshCompanies,
    setShowAddCompany,
  } = useCompanyContext();

  const [rxName, setRxName] = useState("");
  const [step, setStep] = useState("enter"); // "enter" | "review" | "notfound"
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [match, setMatch] = useState(null);
  const [choice, setChoice] = useState("recommended"); // "recommended" | "input"
  const [error, setError] = useState("");

  const busyRef = useRef(false);
  const checkIdRef = useRef(0);
  const reviewHeadingRef = useRef(null);

  const name = rxName.trim();
  const isBusy = isChecking || isSaving;

  // Already added? Compare against both the saved case name and its target so
  // "Ozempic", "ozempic" and "OZEMPIC" all count as the same Rx.
  const existing = name
    ? companies.find(
        (c) =>
          (c.case && sameName(c.case, name)) ||
          (c.target && sameName(c.target, name)),
      )
    : null;
  const isDuplicate = Boolean(existing);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    const previousFocus = document.activeElement;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    const handleEscape = (e) => {
      if (e.key === "Escape" && !busyRef.current) {
        setShowAddCompany(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
      document.removeEventListener("keydown", handleEscape);
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
        previousFocus.focus();
      }
    };
  }, [setShowAddCompany]);

  useEffect(() => {
    if (step !== "enter") reviewHeadingRef.current?.focus();
  }, [step]);

  // Cancel any in-flight check on unmount so it can't set state afterwards.
  useEffect(() => () => {
    checkIdRef.current += 1;
  }, []);

  function handleClose() {
    if (!busyRef.current) setShowAddCompany(false);
  }

  function backToEdit() {
    if (busyRef.current) return;
    setStep("enter");
    setError("");
  }

  async function handleCheck() {
    if (!name || isDuplicate || busyRef.current) return;

    const checkId = ++checkIdRef.current;
    busyRef.current = true;
    setIsChecking(true);
    setError("");

    try {
      const res = await searchRx(name);
      if (checkId !== checkIdRef.current) return;

      const result = extractMatch(res);
      if (!result || !result.recommendedName) {
        setMatch(null);
        setStep("notfound");
        return;
      }

      setMatch(result);
      setChoice("recommended");
      setStep("review");
    } catch (err) {
      if (checkId !== checkIdRef.current) return;
      console.error("Failed to validate Rx:", err);
      setError("We couldn't check that name right now. Please try again.");
    } finally {
      if (checkId === checkIdRef.current) {
        busyRef.current = false;
        setIsChecking(false);
      }
    }
  }

  async function handleConfirm() {
    if (step === "enter" || busyRef.current) return;

    const chosen = chosenName;
    if (!chosen) return;

    busyRef.current = true;
    setIsSaving(true);
    setError("");

    try {
      const savedName = chosen.toLowerCase();
      await addCompany({ name: savedName });

      const updated = await refreshCompanies();
      const added = updated.find((c) => c.case === savedName);

      if (added) selectCompany(added);
      setShowAddCompany(false);
    } catch (err) {
      console.error("Failed to add Rx:", err);
      setError("Unable to add this Rx. Please try again.");
    } finally {
      busyRef.current = false;
      setIsSaving(false);
    }
  }

  const secondaryButtonClass =
    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm text-mutedText transition hover:bg-btnHover hover:text-mainText outline-none ring-inputFocus/60 focus-visible:ring-2 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50";

  // The API lowercases `input_name`, so show and save what the user typed.
  const differs = match && !sameName(name, match.recommendedName);
  const tier = match ? match.tier : "unknown";
  const chosenName =
    step === "notfound"
      ? name
      : match && (choice === "input" ? name : match.recommendedName);

  const optionClass = (selected) =>
    [
      "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition",
      "outline-none ring-inputFocus/60 focus-visible:ring-2",
      selected
        ? "border-velBlue/40 bg-velBlue/10"
        : "border-surfaceBorder bg-innerBg/40 hover:bg-btnHover",
    ].join(" ");

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-mainBg/60 p-4 backdrop-blur-sm sm:p-6"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-rx-title"
        aria-busy={isBusy}
        className="relative flex max-h-[85vh] min-h-[440px] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-surfaceBorder bg-surface shadow-login-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center gap-3 border-b border-mainDivider px-6 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-velBlue/20 bg-velBlue/10">
            <PillBottle className="h-4 w-4 text-velBlue" aria-hidden="true" />
          </div>
          <h2 id="add-rx-title" className="text-lg font-normal text-mainText">
            Add Rx
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isBusy}
            className={`${secondaryButtonClass} ml-auto p-1.5`}
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-7 sm:px-8">
          {step === "notfound" ? (
            <div>
              <h3
                ref={reviewHeadingRef}
                tabIndex={-1}
                className="text-xl font-normal text-mainText outline-none"
              >
                No match found
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-mutedText">
                We couldn't find an Rx matching this name. Check the
                spelling, or add it as typed.
              </p>
              <div className="mt-6 rounded-xl border border-surfaceBorder bg-innerBg/40 px-4 py-4">
                <div className="min-w-0">
                  <span className="block text-xs uppercase tracking-wide text-mutedText">
                    As you typed it
                  </span>
                  <p className="whitespace-pre-wrap break-words text-base font-normal text-mainText">
                    {name}
                  </p>
                </div>
              </div>
            </div>
          ) : step === "review" && match ? (
            <div>
              <h3
                ref={reviewHeadingRef}
                tabIndex={-1}
                className="text-xl font-normal text-mainText outline-none"
              >
                {differs ? "Did you mean this?" : "Does this look right?"}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-mutedText">
                {differs
                  ? "We found a closer match for what you typed. Pick the name you want to add."
                  : "We checked this name against known Rx."}
              </p>

              <div className="mt-6 rounded-xl border border-surfaceBorder bg-innerBg/40 px-4 py-4">
                <ConfidenceMeter tier={tier} />
              </div>

              {differs ? (
                <div
                  role="radiogroup"
                  aria-label="Name to add"
                  className="mt-4 flex flex-col gap-2"
                >
                  <button
                    type="button"
                    role="radio"
                    aria-checked={choice === "recommended"}
                    disabled={isSaving}
                    onClick={() => setChoice("recommended")}
                    className={optionClass(choice === "recommended")}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs uppercase tracking-wide text-mutedText">
                        Recommended
                      </span>
                      <span className="block break-words text-base font-normal text-mainText">
                        {match.recommendedName}
                      </span>
                    </span>
                    {choice === "recommended" && (
                      <Check className="mt-1 h-4 w-4 shrink-0 text-velBlue" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={choice === "input"}
                    disabled={isSaving}
                    onClick={() => setChoice("input")}
                    className={optionClass(choice === "input")}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs uppercase tracking-wide text-mutedText">
                        As you typed it
                      </span>
                      <span className="block break-words text-base font-normal text-mainText">
                        {name}
                      </span>
                    </span>
                    {choice === "input" && (
                      <Check className="mt-1 h-4 w-4 shrink-0 text-velBlue" aria-hidden="true" />
                    )}
                  </button>
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-velBlue/20 bg-velBlue/10 px-4 py-4">
                  <p className="min-w-0 whitespace-pre-wrap break-words text-base font-normal text-mainText">
                    {match.recommendedName}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 className="text-xl font-normal text-mainText">
                What would you like to add?
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-mutedText">
                Enter an Rx name. We'll check it before adding.
              </p>
              <div className="mt-6">
                <Input
                  label="Rx name"
                  value={rxName}
                  onChange={(e) => {
                    setRxName(e.target.value);
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      if (!e.repeat) handleCheck();
                    }
                  }}
                  placeholder="e.g., Ozempic"
                  className="w-full py-3"
                  help={
                    isDuplicate
                      ? `"${existing.case ?? existing.target}" is already in your Rx list.`
                      : undefined
                  }
                />
              </div>
              {isChecking && (
                <p
                  role="status"
                  className="mt-4 flex items-center gap-2 text-sm text-mutedText"
                >
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Checking “{name}”…
                </p>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="mt-4 flex items-start gap-2 text-sm text-errorText">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-mainDivider bg-innerBg/40 px-6 py-4 sm:px-8">
          <button
            type="button"
            disabled={isBusy}
            className={secondaryButtonClass}
            onClick={step === "enter" ? handleClose : backToEdit}
          >
            {step !== "enter" && <ArrowLeft className="h-4 w-4" aria-hidden="true" />}
            {step === "enter" ? "Cancel" : "Edit name"}
          </button>
          {step !== "enter" ? (
            <Button
              key="confirm"
              onClick={handleConfirm}
              disabled={isSaving || !chosenName}
            >
              {isSaving
                ? "Adding..."
                : step === "notfound"
                  ? "Add anyway"
                  : `Add “${chosenName}”`}
            </Button>
          ) : (
            <Button
              key="check"
              onClick={handleCheck}
              disabled={!name || isDuplicate || isChecking}
            >
              {isChecking ? "Checking..." : "Check & continue"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopUpSearch;
